import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useContext,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { DndContext, DragOverlay, useDndMonitor } from "@dnd-kit/core";
import useViewTransform from "./hooks/useViewTransform";
import useKeyboardShortcuts from "./hooks/useKeyboardShortcuts";
import useFramelessDrawing from "./hooks/useFramelessDrawing";
import ConfiguratorToolbar from "./toolbar/ConfiguratorToolbar";
import MobileToolbar from "./toolbar/MobileToolbar";
import SvgCanvas from "./canvas/SvgCanvas";
import StatusBar from "./statusbar/StatusBar";
import PropertiesPanel from "./sidebar/PropertiesPanel";
import ElementSelectionPanel from "./sidebar/ElementSelectionPanel";
import ModuleTemplateSelectionPanel from "./sidebar/ModuleTemplateSelectionPanel";
import FrameSelectionPanel from "./sidebar/FrameSelectionPanel";
import ModulePreviewRenderer from "./sidebar/ModulePreviewRenderer";
import ContextMenu from "./common/ContextMenu";
import EnquiryModal from "./EnquiryModal";
import SaveConfirmModal from "./SaveConfirmModal";
import FramelessCreationModal from "./modals/FramelessCreationModal";
import InstructionModal from "./modals/InstructionModal";
import { useProjectState } from "./state/useProjectState";
import { useUIState } from "./hooks/useUIState";
import { useConfiguratorActions } from "./hooks/useConfiguratorActions";
import { useContextMenuHandler } from "./hooks/useContextMenuHandler";
import { useCostCalculation } from "./hooks/useCostCalculation";
import CostPanel from "./sidebar/CostPanel";
import HistoryPanel from "./sidebar/HistoryPanel";
import {
  OBJECT_TYPES,
  MODES,
  INITIAL_PPM,
  GRID_CELL_SIZE_M,
  FRAMELESS_GRID_CELL_SIZE_M,
  MODULE_DEFAULT_WIDTH,
  MODULE_DEFAULT_LENGTH,
  DEFAULT_MODULE_CELLS_WIDE,
  DEFAULT_MODULE_CELLS_LONG,
  STAIRS_COUNTERPART_MAP,
  EPSILON,
  SANITARY_ELEMENT_TYPES,
  ELECTRICAL_ELEMENT_TYPES,
} from "./configuratorConstants";
import { isSanitaryJoint, isEdgeSegmentInSanitaryJoint } from "./utils/sanitaryCornerUtils";
import { getObjectById } from "./state/projectReducer";
import {
  getModuleVertices,
  checkOverlapWithRotation,
  rotatePoint,
  isPointInModule,
  generateId,
  getLocalCoordinates,
  findDockingPair,
  subtractVectors,
  distance,
  normalizeVector,
  perpendicularVector,
  dotProduct,
  scaleVector,
  addVectors,
  findOverlappingSegment,
  findNearestPointOnLineSegment,
} from "./configuratorUtils";
import { useTranslation } from "react-i18next";
import templateService from "../api/templateService";
import { XMarkIcon, SparklesIcon } from "@heroicons/react/24/outline";
import AuthContext from "../contexts/AuthContext";
import ModularSidebar from "./sidebar/ModularSidebar";
import { useProjectHistory, useElementPlacement, HISTORY_STORAGE_KEY } from "./hooks";

const OVERLAY_WIDTH = 96;
const OVERLAY_HEIGHT = 128;

const DragMonitor = ({ setDragOverlayCoords }) => {
  useDndMonitor({
    onDragMove(event) {
      const pointer =
        event.sensorEvents?.pointer ?? event.sensorEvents?.nativeEvent;
      if (
        pointer &&
        typeof pointer.clientX === "number" &&
        typeof pointer.clientY === "number"
      ) {
        setDragOverlayCoords({ x: pointer.clientX, y: pointer.clientY });
      }
    },
    onDragEnd() {
      setDragOverlayCoords(null);
    },
    onDragCancel() {
      setDragOverlayCoords(null);
    },
  });
  return null;
};

const Configurator = () => {
  const svgRef = useRef(null);
  const mainContainerRef = useRef(null);
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const {
    state,
    dispatch: originalDispatch,
    objectsOnCurrentFloor,
    primarySelectedObject,
    currentFloorIndex,
  } = useProjectState(t);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isInstructionModalOpen, setIsInstructionModalOpen] = useState(false);

  // Функция проверки, что все модули соединены
  const checkAllModulesConnected = useCallback(() => {
    // Только для модульного режима
    if (state.activeMode !== MODES.MODULAR) {
      return true;
    }

    // Собираем все модули со всех этажей
    const allModules = [];
    state.floors.forEach(floor => {
      const floorObjects = state.projectObjects[floor.id] || [];
      const modules = floorObjects.filter(obj => obj.type === OBJECT_TYPES.MODULE);
      allModules.push(...modules);
    });

    // Если модулей нет или только один, проверка не нужна
    if (allModules.length <= 1) {
      return true;
    }

    // Собираем все docking joints со всех этажей
    const allJoints = [];
    state.floors.forEach(floor => {
      const floorObjects = state.projectObjects[floor.id] || [];
      const joints = floorObjects.filter(obj => obj.type === OBJECT_TYPES.DOCKING_JOINT);
      allJoints.push(...joints);
    });

    // Создаем граф связей между модулями
    const moduleConnections = new Map();
    allModules.forEach(module => {
      moduleConnections.set(module.id, new Set());
    });

    // Добавляем связи из docking joints
    allJoints.forEach(joint => {
      if (joint.moduleA_id && joint.moduleB_id) {
        moduleConnections.get(joint.moduleA_id)?.add(joint.moduleB_id);
        moduleConnections.get(joint.moduleB_id)?.add(joint.moduleA_id);
      }
    });

    // Проверяем связность графа методом обхода в глубину (DFS)
    const visited = new Set();
    const dfs = (moduleId) => {
      visited.add(moduleId);
      const connections = moduleConnections.get(moduleId) || new Set();
      connections.forEach(connectedId => {
        if (!visited.has(connectedId)) {
          dfs(connectedId);
        }
      });
    };

    // Начинаем обход с первого модуля
    if (allModules.length > 0) {
      dfs(allModules[0].id);
    }

    // Если посетили все модули, значит все соединены
    return visited.size === allModules.length;
  }, [state.activeMode, state.floors, state.projectObjects]);

  // Функция открытия EnquiryModal с проверкой
  const handleOpenEnquiryModal = useCallback(() => {
    if (!checkAllModulesConnected()) {
      toast.warning(t('toasts.modulesNotConnected'), {
        autoClose: 5000,
      });
      return;
    }
    setIsEnquiryModalOpen(true);
  }, [checkAllModulesConnected, t]);
  const [isMobilePropertiesOpen, setIsMobilePropertiesOpen] = useState(false);
  const [isMobileElementsOpen, setIsMobileElementsOpen] = useState(false);
  const [isAiPanelOpen, setIsAiPanelOpen] = useState(false);
  const [aiQuery, setAiQuery] = useState("");
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [framelessModalState, setFramelessModalState] = useState({
    isOpen: false,
    position: null,
  });
  const [activeDragTemplate, setActiveDragTemplate] = useState(null);
  const [activeDragFrame, setActiveDragFrame] = useState(null);
  const [dragOverlayCoords, setDragOverlayCoords] = useState(null);
  const [moduleToMove, setModuleToMove] = useState(null); // Модуль в процессе перемещения (шаблон)
  const [originalModuleId, setOriginalModuleId] = useState(null); // ID оригинального модуля для удаления
  const [savedModuleForMove, setSavedModuleForMove] = useState(null); // Сохраненный модуль для восстановления
  const [ghostModulePosition, setGhostModulePosition] = useState(null); // Позиция ghost модуля (следует за курсором)

  const dispatch = useCallback(
    (action) => {
      originalDispatch({ ...action, t });
    },
    [originalDispatch, t],
  );

  const { viewTransform, setViewTransform, screenToWorld } =
    useViewTransform(svgRef);
  const { totalCost, costBreakdown } = useCostCalculation(state);
  const uiState = useUIState();

  const {
    contextMenu,
    setContextMenu,
    elementTypeToPlace,
    setElementTypeToPlace,
    closeContextMenu,
    isPlacementModeActive,
    setIsPlacementModeActive,
    placementSlots,
    setPlacementSlots,
    exitPlacementMode,
    isHistoryPanelOpen,
    setIsHistoryPanelOpen,
  } = uiState;

  const customCollisionDetection = useCallback(
    (args) => {
      const pointerPosition = args.pointerCoordinates;
      if (!pointerPosition || !placementSlots.length) {
        return [];
      }

      const pointerWorldPosition = screenToWorld(
        pointerPosition.x,
        pointerPosition.y,
      );

      const intersectingSlot = placementSlots.find((slot) => {
        const localCoords = getLocalCoordinates(
          pointerWorldPosition.x,
          pointerWorldPosition.y,
          slot,
        );
        return (
          localCoords.x >= 0 &&
          localCoords.x <= slot.width &&
          localCoords.y >= 0 &&
          localCoords.y <= slot.height
        );
      });

      if (intersectingSlot) {
        const droppableContainer = args.droppableContainers.find(
          (container) => container.id === `slot-${intersectingSlot.id}`,
        );
        return droppableContainer ? [droppableContainer] : [];
      }

      return [];
    },
    [placementSlots, screenToWorld],
  );

  const openFramelessCreationModal = useCallback((position) => {
    setFramelessModalState({ isOpen: true, position });
  }, []);

  const actions = useConfiguratorActions(
    dispatch,
    state,
    {
      setIsTemplatePanelOpen: () => { },
      setActivePlacementSlot: () => { },
    },
    availableTemplates
  );
  const {
    loadFileInputRef,
    handleLoadProject,
    triggerLoadFile,
    submitAiQuery,
  } = actions;

  const handleContextMenuAction = useContextMenuHandler(
    state,
    dispatch,
    uiState,
    { setContextMenu },
    screenToWorld,
  );

  useFramelessDrawing({
    svgRef,
    isActive: state.activeMode === MODES.FRAMELESS && elementTypeToPlace === OBJECT_TYPES.FRAMELESS_WALL_TOOL,
    elementTypeToPlace,
    dispatch,
    screenToWorld,
    projectObjects: state.projectObjects,
    currentFloorId: state.currentFloorId,
  });

  const handleCreateFramelessBuilding = (dimensions, position, suggestions) => {
    dispatch({
      type: "CREATE_FRAMELESS_BUILDING",
      payload: { dimensions, position, suggestions },
    });
  };

  const objectsToRender = useMemo(() => {
    if (state.activeMode === MODES.FRAMELESS) {
      const buildings = objectsOnCurrentFloor.filter(
        (obj) => obj.type === OBJECT_TYPES.FRAMELESS_BUILDING,
      );
      const buildingWalls = buildings.flatMap((b) => [
        ...(b.perimeterWalls || []),
        ...(b.internalWalls || []),
      ]);
      // Также включаем стены, созданные напрямую (не в здании)
      const directWalls = objectsOnCurrentFloor.filter(
        (obj) => obj.type === OBJECT_TYPES.FRAMELESS_WALL,
      );
      const partitions = objectsOnCurrentFloor.filter(
        (obj) => obj.type === OBJECT_TYPES.FRAMELESS_PARTITION,
      );
      return [...buildingWalls, ...directWalls, ...partitions];
    }
    return objectsOnCurrentFloor;
  }, [objectsOnCurrentFloor, state.activeMode]);

  // Проверяем наличие комнат в безкаркасном режиме
  const hasRooms = useMemo(() => {
    if (state.activeMode !== MODES.FRAMELESS) return false;

    const allWalls = objectsToRender.filter(
      (obj) => obj.type === OBJECT_TYPES.FRAMELESS_WALL
    );

    if (allWalls.length < 4) return false;

    // Функция для поиска компонентов связанных стен
    const findConnectedWalls = (startWall, walls) => {
      const component = new Set([startWall]);
      const stack = [startWall];
      const visited = new Set([startWall.id]);
      const epsilon = 0.001;

      while (stack.length > 0) {
        const currentWall = stack.pop();

        walls.forEach(otherWall => {
          if (visited.has(otherWall.id)) return;

          const hasConnection =
            distance(currentWall.startPoint, otherWall.startPoint) < epsilon ||
            distance(currentWall.startPoint, otherWall.endPoint) < epsilon ||
            distance(currentWall.endPoint, otherWall.startPoint) < epsilon ||
            distance(currentWall.endPoint, otherWall.endPoint) < epsilon;

          if (hasConnection) {
            visited.add(otherWall.id);
            component.add(otherWall);
            stack.push(otherWall);
          }
        });
      }

      return Array.from(component);
    };

    // Функция для проверки, есть ли в компоненте замкнутый цикл
    const hasClosedLoop = (walls) => {
      if (walls.length < 4) return false;

      const epsilon = 0.001;
      const pointMap = new Map();

      walls.forEach(wall => {
        const startKey = `${Math.round(wall.startPoint.x * 100)},${Math.round(wall.startPoint.y * 100)}`;
        const endKey = `${Math.round(wall.endPoint.x * 100)},${Math.round(wall.endPoint.y * 100)}`;
        pointMap.set(startKey, (pointMap.get(startKey) || 0) + 1);
        pointMap.set(endKey, (pointMap.get(endKey) || 0) + 1);
      });

      // Упрощенная проверка: если есть хотя бы 4 стены и минимум 4 точки соединения,
      // и хотя бы 4 точки имеют 2 соединения (признак углов), то считаем, что есть комната
      // Это работает даже если есть дополнительные незамкнутые стены
      const pointsWithTwoConnections = Array.from(pointMap.values()).filter(count => count === 2).length;
      const totalPoints = pointMap.size;

      // Для замкнутого прямоугольника: 4 стены, 4 угла (точки с 2 соединениями)
      // Если есть дополнительные стены, точек может быть больше, но углов должно быть минимум 4
      return totalPoints >= 4 && pointsWithTwoConnections >= 4;
    };

    // Находим все компоненты связанных стен
    const visited = new Set();
    for (const wall of allWalls) {
      if (visited.has(wall.id)) continue;

      const component = findConnectedWalls(wall, allWalls);
      component.forEach(w => visited.add(w.id));

      // Проверяем, есть ли в этом компоненте замкнутый цикл
      if (hasClosedLoop(component)) {
        return true;
      }
    }

    return false;
  }, [objectsToRender, state.activeMode]);

  // Вычисляем слоты для внутренних стен внутри комнат
  // Слоты - это линии сетки внутри комнаты, вдоль которых можно разместить внутренние стены
  const internalWallSlots = useMemo(() => {
    if (state.activeMode !== MODES.FRAMELESS || elementTypeToPlace !== OBJECT_TYPES.INTERNAL_WALL_TOOL || !hasRooms) {
      return [];
    }

    // Находим все замкнутые циклы (комнаты)
    const allWalls = objectsToRender.filter(
      (obj) => obj.type === OBJECT_TYPES.FRAMELESS_WALL
    );

    // Правильная функция для поиска всех замкнутых циклов (комнат)
    // Использует DFS для поиска всех возможных замкнутых ортогональных циклов
    const findAllClosedLoops = (walls) => {
      const loops = [];
      const epsilon = 0.001;

      // Вспомогательная функция для получения конечной точки в зависимости от направления
      const getNextPoint = (wall, fromPoint) => {
        const distToStart = distance(fromPoint, wall.startPoint);
        const distToEnd = distance(fromPoint, wall.endPoint);

        if (distToStart < epsilon) {
          return wall.endPoint; // Идём от start к end
        } else if (distToEnd < epsilon) {
          return wall.startPoint; // Идём от end к start
        }
        return null;
      };

      // Функция для проверки ортогональности цикла
      const checkIfWallsFormClosedOrthogonalLoop = (loopWalls) => {
        if (loopWalls.length < 4) return false;

        const epsilon = 0.001;
        for (let i = 0; i < loopWalls.length; i++) {
          const wall = loopWalls[i];
          const dx = Math.abs(wall.endPoint.x - wall.startPoint.x);
          const dy = Math.abs(wall.endPoint.y - wall.startPoint.y);

          // Стена должна быть либо горизонтальной, либо вертикальной
          if (dx > epsilon && dy > epsilon) {
            return false; // Диагональная стена
          }
        }
        return true;
      };

      // Пробуем найти все возможные замкнутые ортогональные циклы
      for (let i = 0; i < walls.length; i++) {
        const startWall = walls[i];

        // Пробуем оба направления для начальной стены
        for (const startPoint of [startWall.startPoint, startWall.endPoint]) {
          const visited = new Set([startWall.id]);
          const path = [startWall];
          const currentPoint = getNextPoint(startWall, startPoint);
          if (!currentPoint) continue;

          const dfs = (currentWall, currentEnd) => {
            // Проверяем, можем ли замкнуть цикл
            if (path.length >= 4) {
              const distToStart = distance(currentEnd, startPoint);
              if (distToStart < epsilon) {
                // Нашли замкнутый цикл!
                const loop = [...path];
                // Проверяем, что это ортогональный замкнутый контур
                if (checkIfWallsFormClosedOrthogonalLoop(loop)) {
                  // Проверяем, не добавили ли мы уже этот цикл
                  const loopKey = loop.map(w => w.id).sort().join('|');
                  const isDuplicate = loops.some(existingLoop => {
                    const existingKey = existingLoop.map(w => w.id).sort().join('|');
                    return existingKey === loopKey;
                  });
                  if (!isDuplicate) {
                    loops.push(loop);
                  }
                }
                return;
              }
            }

            if (path.length >= 10) return; // Ограничение глубины поиска

            // Ищем следующую стену, которая соединяется с текущей точкой
            for (const nextWall of walls) {
              if (visited.has(nextWall.id)) continue;

              const nextPoint = getNextPoint(nextWall, currentEnd);
              if (nextPoint) {
                visited.add(nextWall.id);
                path.push(nextWall);
                dfs(nextWall, nextPoint);
                path.pop();
                visited.delete(nextWall.id);
              }
            }
          };

          dfs(startWall, currentPoint);
        }
      }

      // Вычисляем границы и площадь каждого цикла
      const loopsWithBounds = loops.map(loop => {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        loop.forEach(wall => {
          minX = Math.min(minX, wall.startPoint.x, wall.endPoint.x);
          minY = Math.min(minY, wall.startPoint.y, wall.endPoint.y);
          maxX = Math.max(maxX, wall.startPoint.x, wall.endPoint.x);
          maxY = Math.max(maxY, wall.startPoint.y, wall.endPoint.y);
        });
        const area = (maxX - minX) * (maxY - minY);
        return { loop, minX, minY, maxX, maxY, area };
      });

      // Фильтруем: оставляем только минимальные (не содержащие других циклов)
      // Но сохраняем смежные комнаты, которые используют общие стены
      const minimalLoops = loopsWithBounds.filter((loopData, index) => {
        const { loop, minX, minY, maxX, maxY, area } = loopData;

        // Проверяем, не содержит ли этот цикл другие циклы
        for (let j = 0; j < loopsWithBounds.length; j++) {
          if (j === index) continue;

          const other = loopsWithBounds[j];
          const otherLoop = other.loop;

          // Проверяем, используют ли циклы общие стены
          const loopWallIds = new Set(loop.map(w => w.id));
          const otherLoopWallIds = new Set(otherLoop.map(w => w.id));
          const sharedWalls = [...loopWallIds].filter(id => otherLoopWallIds.has(id));

          // Если циклы используют общие стены - это смежные комнаты, оба должны быть сохранены
          if (sharedWalls.length > 0) {
            continue; // Пропускаем проверку для смежных комнат
          }

          // Если другой цикл имеет меньшую площадь и находится полностью внутри текущего
          // И они не используют общие стены (т.е. одна комната действительно внутри другой)
          if (other.area < area) {
            const isInside =
              other.minX >= minX - 0.01 &&
              other.maxX <= maxX + 0.01 &&
              other.minY >= minY - 0.01 &&
              other.maxY <= maxY + 0.01;

            if (isInside) {
              return false;
            }
          }
        }

        return true;
      });

      return minimalLoops.map(data => data.loop);
    };

    const closedLoops = findAllClosedLoops(allWalls);

    if (closedLoops.length === 0) {
      return [];
    }

    const slots = [];

    // Для каждой комнаты создаем линии сетки внутри неё
    closedLoops.forEach((roomWalls, roomIdx) => {
      // Вычисляем границы комнаты
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      roomWalls.forEach(wall => {
        minX = Math.min(minX, wall.startPoint.x, wall.endPoint.x);
        minY = Math.min(minY, wall.startPoint.y, wall.endPoint.y);
        maxX = Math.max(maxX, wall.startPoint.x, wall.endPoint.x);
        maxY = Math.max(maxY, wall.startPoint.y, wall.endPoint.y);
      });

      // Создаем отдельные слоты для каждого сегмента сетки внутри комнаты
      // Горизонтальные слоты (параллельно верхней/нижней стене)
      const startY = Math.ceil(minY / FRAMELESS_GRID_CELL_SIZE_M) * FRAMELESS_GRID_CELL_SIZE_M;
      const endY = Math.floor(maxY / FRAMELESS_GRID_CELL_SIZE_M) * FRAMELESS_GRID_CELL_SIZE_M;

      for (let y = startY; y < endY; y += FRAMELESS_GRID_CELL_SIZE_M) {
        // Пропускаем линии, которые совпадают с границами комнаты
        if (Math.abs(y - minY) < 0.01 || Math.abs(y - maxY) < 0.01) continue;

        // Создаем отдельные слоты для каждого сегмента сетки по горизонтали
        const startX = Math.ceil(minX / FRAMELESS_GRID_CELL_SIZE_M) * FRAMELESS_GRID_CELL_SIZE_M;
        const endX = Math.floor(maxX / FRAMELESS_GRID_CELL_SIZE_M) * FRAMELESS_GRID_CELL_SIZE_M;

        for (let x = startX; x < endX; x += FRAMELESS_GRID_CELL_SIZE_M) {
          const segmentStart = { x: x, y: y };
          const segmentEnd = { x: x + FRAMELESS_GRID_CELL_SIZE_M, y: y };

          // Проверяем, что сегмент не выходит за границы комнаты
          if (segmentEnd.x > maxX + 0.01) continue;

          slots.push({
            id: `internal-wall-segment-h-${roomIdx}-${x.toFixed(2)}-${y.toFixed(2)}`,
            type: 'horizontal',
            startPoint: segmentStart,
            endPoint: segmentEnd,
            roomIndex: roomIdx
          });
        }
      }

      // Вертикальные слоты (параллельно левой/правой стене)
      const startX = Math.ceil(minX / FRAMELESS_GRID_CELL_SIZE_M) * FRAMELESS_GRID_CELL_SIZE_M;
      const endX = Math.floor(maxX / FRAMELESS_GRID_CELL_SIZE_M) * FRAMELESS_GRID_CELL_SIZE_M;

      for (let x = startX; x < endX; x += FRAMELESS_GRID_CELL_SIZE_M) {
        // Пропускаем линии, которые совпадают с границами комнаты
        if (Math.abs(x - minX) < 0.01 || Math.abs(x - maxX) < 0.01) continue;

        // Создаем отдельные слоты для каждого сегмента сетки по вертикали
        const startY = Math.ceil(minY / FRAMELESS_GRID_CELL_SIZE_M) * FRAMELESS_GRID_CELL_SIZE_M;
        const endY = Math.floor(maxY / FRAMELESS_GRID_CELL_SIZE_M) * FRAMELESS_GRID_CELL_SIZE_M;

        for (let y = startY; y < endY; y += FRAMELESS_GRID_CELL_SIZE_M) {
          const segmentStart = { x: x, y: y };
          const segmentEnd = { x: x, y: y + FRAMELESS_GRID_CELL_SIZE_M };

          // Проверяем, что сегмент не выходит за границы комнаты
          if (segmentEnd.y > maxY + 0.01) continue;

          slots.push({
            id: `internal-wall-segment-v-${roomIdx}-${x.toFixed(2)}-${y.toFixed(2)}`,
            type: 'vertical',
            startPoint: segmentStart,
            endPoint: segmentEnd,
            roomIndex: roomIdx
          });
        }
      }
    });

    return slots;
  }, [objectsToRender, state.activeMode, elementTypeToPlace, hasRooms]);


  useEffect(() => {
    if (primarySelectedObject && !isMobilePropertiesOpen) {
      setIsMobilePropertiesOpen(true);
    } else if (!primarySelectedObject && isMobilePropertiesOpen) {
      setIsMobilePropertiesOpen(false);
    }
  }, [primarySelectedObject, isMobilePropertiesOpen]);


  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await templateService.getPublicTemplates();
        const parsedTemplates = response.data.map((tpl) => ({
          ...tpl,
          config:
            typeof tpl.config === "string"
              ? JSON.parse(tpl.config)
              : tpl.config,
        }));

        // Всі шаблони тепер завантажуються з БД
        setAvailableTemplates([...parsedTemplates]);
      } catch (error) {
        console.error("Failed to fetch templates", error);
        toast.error("Не вдалося завантажити шаблони модулів.");
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (currentFloorIndex > 0 && availableTemplates.length > 0) {
      const objectsOnThisFloor =
        state.projectObjects[state.currentFloorId] || [];
      const prevFloorId = state.floors[currentFloorIndex - 1].id;
      const objectsOnPrevFloor = state.projectObjects[prevFloorId] || [];
      const stairsOnPrevFloor = objectsOnPrevFloor.filter(
        (obj) =>
          obj.type === OBJECT_TYPES.MODULE &&
          STAIRS_COUNTERPART_MAP[obj.templateId],
      );

      stairsOnPrevFloor.forEach((stairGhost) => {
        const counterpartId = `derived_${stairGhost.id}`;
        const counterpartAlreadyExists = objectsOnThisFloor.some(
          (obj) => obj.id === counterpartId,
        );

        if (!counterpartAlreadyExists) {
          const counterpartTemplate = availableTemplates.find(
            (t) =>
              t.config.id === STAIRS_COUNTERPART_MAP[stairGhost.templateId],
          );
          if (counterpartTemplate) {
            dispatch({
              type: "ADD_PRE_DERIVED_STAIRS",
              payload: {
                ghostModule: stairGhost,
                template: counterpartTemplate,
              },
            });
          }
        }
      });
    }
  }, [
    currentFloorIndex,
    state.floors,
    state.projectObjects,
    availableTemplates,
    dispatch,
    state.currentFloorId,
  ]);

  const handleAiSubmit = async () => {
    if (!aiQuery.trim()) {
      toast.warn("Будь ласка, введіть запит для AI.");
      return;
    }
    setIsAiLoading(true);
    try {
      await submitAiQuery(aiQuery);
      setIsAiPanelOpen(false);
      setAiQuery("");
    } catch (error) {
      console.error("AI submission failed in component:", error);
    } finally {
      setIsAiLoading(false);
    }
  };

  const { handleLoadFromHistory, handleDeleteFromHistory } = useProjectHistory({
    dispatch,
    setIsHistoryPanelOpen,
    t,
  });

  const { handleWallSegmentClickForPlacement, checkForWindowNearby } =
    useElementPlacement({
      elementTypeToPlace,
      setElementTypeToPlace,
      state,
      dispatch,
      currentFloorIndex,
      t,
      objectsOnCurrentFloor,
      availableTemplates,
      objectsToRender,
      hasRooms,
    });

  const isModuleFullySupported = useCallback(
    (moduleToCheck, supportingModules) => {
      if (currentFloorIndex === 0) return true;
      if (supportingModules.length === 0 && currentFloorIndex > 0) return false;
      const vertices = getModuleVertices(moduleToCheck);
      return vertices.every((vertex) =>
        supportingModules.some((ghostModule) =>
          isPointInModule(vertex, ghostModule),
        ),
      );
    },
    [currentFloorIndex],
  );

  const ghostsFromBelow = useMemo(() => {
    if (currentFloorIndex > 0) {
      const prevFloorId = state.floors[currentFloorIndex - 1].id;
      return state.projectObjects[prevFloorId] || [];
    }
    return [];
  }, [currentFloorIndex, state.floors, state.projectObjects]);

  const canShowInitialModuleButton = useMemo(
    () =>
      state.activeMode === MODES.MODULAR &&
      objectsOnCurrentFloor.filter((obj) => obj.type === OBJECT_TYPES.MODULE)
        .length === 0,
    [state.activeMode, objectsOnCurrentFloor],
  );

  const initialModulePlacementCoords = useMemo(() => {
    if (!canShowInitialModuleButton) return null;

    if (currentFloorIndex === 0) {
      return { worldX: 0, worldY: 0 };
    }

    const ghostModulesFromBelow = ghostsFromBelow.filter(
      (o) => o.type === OBJECT_TYPES.MODULE,
    );
    if (ghostModulesFromBelow.length === 0) {
      toast.info(t("toasts.noModulesBelow"));
      return null;
    }

    const stairsModuleBelow = ghostModulesFromBelow.find(
      (m) => m.subtype === "stairs",
    );
    if (stairsModuleBelow) {
      const vertices = getModuleVertices(stairsModuleBelow);
      const centerX = vertices.reduce((sum, v) => sum + v.x, 0) / 4;
      const centerY = vertices.reduce((sum, v) => sum + v.y, 0) / 4;
      const snappedX =
        Math.round(centerX / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;
      const snappedY =
        Math.round(centerY / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;

      const potentialButtonModuleShape = {
        x: snappedX,
        y: snappedY,
        rotation: stairsModuleBelow.rotation || 0,
        width: MODULE_DEFAULT_WIDTH,
        height: MODULE_DEFAULT_LENGTH,
      };
      if (
        isModuleFullySupported(
          potentialButtonModuleShape,
          ghostModulesFromBelow,
        )
      ) {
        return { worldX: snappedX, worldY: snappedY };
      }
    }

    let totalArea = 0,
      weightedCenterX = 0,
      weightedCenterY = 0;
    ghostModulesFromBelow.forEach((module) => {
      const area = module.width * module.height;
      const vertices = getModuleVertices(module);
      const moduleCenterX = vertices.reduce((sum, v) => sum + v.x, 0) / 4;
      const moduleCenterY = vertices.reduce((sum, v) => sum + v.y, 0) / 4;
      weightedCenterX += moduleCenterX * area;
      weightedCenterY += moduleCenterY * area;
      totalArea += area;
    });

    if (totalArea > 0) {
      const trueCenterX = weightedCenterX / totalArea;
      const trueCenterY = weightedCenterY / totalArea;
      const snappedX =
        Math.round(trueCenterX / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;
      const snappedY =
        Math.round(trueCenterY / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;

      const potentialButtonModuleShape = {
        x: snappedX,
        y: snappedY,
        rotation: 0,
        width: MODULE_DEFAULT_WIDTH,
        height: MODULE_DEFAULT_LENGTH,
      };
      if (
        isModuleFullySupported(
          potentialButtonModuleShape,
          ghostModulesFromBelow,
        )
      ) {
        return { worldX: snappedX, worldY: snappedY };
      }
    }

    toast.info(t("toasts.noSuitablePlacement"));
    return null;
  }, [
    canShowInitialModuleButton,
    currentFloorIndex,
    ghostsFromBelow,
    isModuleFullySupported,
    t,
  ]);

  const calculateAndSetPlacementSlots = useCallback(
    (draggedTemplate = null) => {
      // Исключаем перемещаемый модуль из расчетов слотов
      const modulesOnThisFloor = objectsOnCurrentFloor.filter(
        (o) => o.type === OBJECT_TYPES.MODULE && o.id !== originalModuleId,
      );
      const newSlots = [];
      const slotKeySet = new Set();
      const newModuleConfig = draggedTemplate?.config;

      // Определяем ориентацию первого модуля (по размерам, а не по rotation!)
      const firstModule = modulesOnThisFloor.length > 0 ? modulesOnThisFloor[0] : null;
      const firstModuleIsHorizontal = firstModule && firstModule.cellsWide > firstModule.cellsLong;

      // Если первый модуль горизонтальный, новые модули тоже должны быть горизонтальными
      let adjustedCellsWide = newModuleConfig ? newModuleConfig.cellsWide : DEFAULT_MODULE_CELLS_WIDE;
      let adjustedCellsLong = newModuleConfig ? newModuleConfig.cellsLong : DEFAULT_MODULE_CELLS_LONG;

      // Подменяем размеры, если первый модуль горизонтальный, а шаблон вертикальный
      if (firstModuleIsHorizontal && adjustedCellsWide < adjustedCellsLong) {
        // Меняем местами: шаблон вертикальный (2x5), делаем горизонтальным (5x2)
        [adjustedCellsWide, adjustedCellsLong] = [adjustedCellsLong, adjustedCellsWide];
      }

      const newModuleWidth = adjustedCellsWide * GRID_CELL_SIZE_M;
      const newModuleHeight = adjustedCellsLong * GRID_CELL_SIZE_M;

      // Стандартные размеры модуля (доступны для всей функции)
      const standardVerticalWidth = 2 * GRID_CELL_SIZE_M;  // 2.44
      const standardVerticalHeight = 5 * GRID_CELL_SIZE_M;  // 6.1
      const standardHorizontalWidth = 5 * GRID_CELL_SIZE_M;  // 6.1
      const standardHorizontalHeight = 2 * GRID_CELL_SIZE_M;  // 2.44

      if (draggedTemplate?.config?.id === 'corridor_module_4x2') {
        const standardModules = objectsOnCurrentFloor.filter(
          (o) => o.type === OBJECT_TYPES.MODULE &&
            o.cellsWide === 2 &&
            o.cellsLong === 5,
        );

        if (standardModules.length >= 2) {
          for (let i = 0; i < standardModules.length; i++) {
            for (let j = i + 1; j < standardModules.length; j++) {
              const moduleA = standardModules[i];
              const moduleB = standardModules[j];
              const dockingInfo = findDockingPair(moduleA, moduleB);

              if (dockingInfo) {
                const jointLength = distance(
                  dockingInfo.overlapSegment[0],
                  dockingInfo.overlapSegment[1],
                );

                if (Math.abs(jointLength - 5 * GRID_CELL_SIZE_M) < EPSILON) {
                  const corridorLongSide = newModuleWidth;
                  const corridorShortSide = newModuleHeight;

                  const [p1, p2] = dockingInfo.overlapSegment;
                  const jointCenter = {
                    x: (p1.x + p2.x) / 2,
                    y: (p1.y + p2.y) / 2,
                  };
                  const jointVector = subtractVectors(p2, p1);
                  const jointNormal = normalizeVector(
                    perpendicularVector(jointVector),
                  );

                  const slotRotation =
                    Math.atan2(jointNormal.y, jointNormal.x) * (180 / Math.PI);

                  const halfJointVec = scaleVector(
                    normalizeVector(jointVector),
                    jointLength / 2,
                  );
                  const endCenter1 = addVectors(jointCenter, halfJointVec);
                  const endCenter2 = subtractVectors(jointCenter, halfJointVec);

                  const offsetDistance = corridorShortSide / 2;
                  const finalCenter1 = addVectors(
                    endCenter1,
                    scaleVector(normalizeVector(jointVector), offsetDistance),
                  );
                  const finalCenter2 = subtractVectors(
                    endCenter2,
                    scaleVector(normalizeVector(jointVector), offsetDistance),
                  );

                  for (const center of [finalCenter1, finalCenter2]) {
                    const localCenterVec = {
                      x: corridorLongSide / 2,
                      y: corridorShortSide / 2,
                    };
                    const rotatedCenterVec = rotatePoint(
                      localCenterVec,
                      { x: 0, y: 0 },
                      slotRotation,
                    );
                    const topLeft = subtractVectors(center, rotatedCenterVec);

                    const newSlot = {
                      id: generateId("slot_"),
                      ...topLeft,
                      rotation: slotRotation,
                      width: corridorLongSide,
                      height: corridorShortSide,
                    };

                    const hasCollision = modulesOnThisFloor.some(
                      (existingModule) =>
                        checkOverlapWithRotation(newSlot, existingModule),
                    );

                    if (!hasCollision) {
                      newSlots.push(newSlot);
                    }
                  }
                }
              }
            }
          }
        }

        if (newSlots.length > 0) {
          const uniqueSlots = Array.from(
            new Map(
              newSlots.map((slot) => [
                `${slot.x.toFixed(2)},${slot.y.toFixed(2)}`,
                slot,
              ]),
            ).values(),
          );
          setPlacementSlots(uniqueSlots);
          setIsPlacementModeActive(true);
        } else {
          toast.info(
            "Для розміщення широкого коридору (4.8м) потрібен ідеальний стик двох стандартних модулів (2x5) по їх ДОВГІЙ стороні (6м).",
          );
          exitPlacementMode();
        }
        return;
      }

      if (currentFloorIndex > 0 && modulesOnThisFloor.length === 0) {
        const ghostModulesFromBelow = ghostsFromBelow.filter(
          (o) => o.type === OBJECT_TYPES.MODULE,
        );
        if (ghostModulesFromBelow.length === 0) {
          if (draggedTemplate) toast.info(t("toasts.noModulesBelow"));
          setPlacementSlots([]);
          setIsPlacementModeActive(false);
          return;
        }
        if (!newModuleConfig) {
          setPlacementSlots([]);
          setIsPlacementModeActive(false);
          return;
        }

        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity;
        ghostModulesFromBelow.forEach((ghost) => {
          const vertices = getModuleVertices(ghost);
          vertices.forEach((v) => {
            minX = Math.min(minX, v.x);
            minY = Math.min(minY, v.y);
            maxX = Math.max(maxX, v.x);
            maxY = Math.max(maxY, v.y);
          });
        });

        for (let x = minX; x <= maxX; x += GRID_CELL_SIZE_M) {
          for (let y = minY; y <= maxY; y += GRID_CELL_SIZE_M) {
            for (const rot of [0, 90, 180, 270]) {
              const candidateSlot = {
                id: generateId("slot_"),
                x: x,
                y: y,
                rotation: rot,
                width: newModuleWidth,
                height: newModuleHeight,
              };
              if (
                isModuleFullySupported(candidateSlot, ghostModulesFromBelow)
              ) {
                const slotKey = `${candidateSlot.x.toFixed(2)}-${candidateSlot.y.toFixed(2)}-${candidateSlot.rotation}`;
                if (!slotKeySet.has(slotKey)) {
                  newSlots.push(candidateSlot);
                  slotKeySet.add(slotKey);
                }
              }
            }
          }
        }
      } else {
        const modulesToBuildAround =
          modulesOnThisFloor.length > 0 ? modulesOnThisFloor : [];

        // Если пол пустой (после удаления модуля для перемещения), показываем только initial slot
        if (modulesOnThisFloor.length === 0) {
          // Вычисляем координаты для initial slot
          let initialSlotCoords = null;

          if (currentFloorIndex === 0) {
            initialSlotCoords = { worldX: 0, worldY: 0 };
          } else {
            const ghostModulesFromBelow = ghostsFromBelow.filter(
              (o) => o.type === OBJECT_TYPES.MODULE,
            );
            if (ghostModulesFromBelow.length > 0) {
              const stairsModuleBelow = ghostModulesFromBelow.find(
                (m) => m.subtype === "stairs",
              );
              if (stairsModuleBelow) {
                const vertices = getModuleVertices(stairsModuleBelow);
                const centerX = vertices.reduce((sum, v) => sum + v.x, 0) / 4;
                const centerY = vertices.reduce((sum, v) => sum + v.y, 0) / 4;
                const snappedX =
                  Math.round(centerX / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;
                const snappedY =
                  Math.round(centerY / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;

                const potentialButtonModuleShape = {
                  x: snappedX,
                  y: snappedY,
                  rotation: stairsModuleBelow.rotation || 0,
                  width: newModuleWidth,
                  height: newModuleHeight,
                };
                if (
                  isModuleFullySupported(
                    potentialButtonModuleShape,
                    ghostModulesFromBelow,
                  )
                ) {
                  initialSlotCoords = { worldX: snappedX, worldY: snappedY };
                }
              }

              if (!initialSlotCoords) {
                let totalArea = 0,
                  weightedCenterX = 0,
                  weightedCenterY = 0;
                ghostModulesFromBelow.forEach((module) => {
                  const area = module.width * module.height;
                  const vertices = getModuleVertices(module);
                  const moduleCenterX = vertices.reduce((sum, v) => sum + v.x, 0) / 4;
                  const moduleCenterY = vertices.reduce((sum, v) => sum + v.y, 0) / 4;
                  weightedCenterX += moduleCenterX * area;
                  weightedCenterY += moduleCenterY * area;
                  totalArea += area;
                });

                if (totalArea > 0) {
                  const trueCenterX = weightedCenterX / totalArea;
                  const trueCenterY = weightedCenterY / totalArea;
                  const snappedX =
                    Math.round(trueCenterX / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;
                  const snappedY =
                    Math.round(trueCenterY / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;

                  const potentialButtonModuleShape = {
                    x: snappedX,
                    y: snappedY,
                    rotation: 0,
                    width: newModuleWidth,
                    height: newModuleHeight,
                  };
                  if (
                    isModuleFullySupported(
                      potentialButtonModuleShape,
                      ghostModulesFromBelow,
                    )
                  ) {
                    initialSlotCoords = { worldX: snappedX, worldY: snappedY };
                  }
                }
              }
            }
          }

          // Если нашли координаты, создаем initial slot
          if (initialSlotCoords) {
            const initialSlot = {
              id: "initial_slot",
              x: initialSlotCoords.worldX,
              y: initialSlotCoords.worldY,
              rotation: 0,
              width: newModuleWidth,
              height: newModuleHeight,
              anchorModuleId: null,
            };
            newSlots.push(initialSlot);
          }
          // Если пол пустой, не создаем слоты вокруг модулей - только initial slot
        } else {
          // Создаем слоты вокруг существующих модулей только если пол не пустой

          // Для коридора при перемещении модуля нужно генерировать слоты для обеих ориентаций
          // Определяем, является ли перемещаемый модуль стандартным модулем (2x5 или 5x2)
          const isMovingModule = originalModuleId !== null;
          const isStandardModule = isMovingModule && newModuleConfig &&
            ((newModuleConfig.cellsWide === 2 && newModuleConfig.cellsLong === 5) ||
              (newModuleConfig.cellsWide === 5 && newModuleConfig.cellsLong === 2));

          modulesToBuildAround.forEach((module) => {
            const { x, y, width, height, rotation = 0 } = module;
            const isCorridorModule = module.subtype === 'corridor';

            const potentialSlotsData = [
              {
                side: "top",
                dx: (width - newModuleWidth) / 2,
                dy: -newModuleHeight,
                rot: 0,
                relevantSide: "width",
              },
              {
                side: "right",
                dx: width,
                dy: (height - newModuleHeight) / 2,
                rot: 0,
                relevantSide: "height",
              },
              {
                side: "bottom",
                dx: (width - newModuleWidth) / 2,
                dy: height,
                rot: 0,
                relevantSide: "width",
              },
              {
                side: "left",
                dx: -newModuleWidth,
                dy: (height - newModuleHeight) / 2,
                rot: 0,
                relevantSide: "height",
              },
            ];
            // Для стандартных модулей при перемещении генерируем слоты для обеих ориентаций
            // Создаем список ориентаций для обработки
            const orientationsToTry = isStandardModule ? [
              { width: standardVerticalWidth, height: standardVerticalHeight, name: 'vertical' },
              { width: standardHorizontalWidth, height: standardHorizontalHeight, name: 'horizontal' }
            ] : [
              { width: newModuleWidth, height: newModuleHeight, name: 'default' }
            ];

            orientationsToTry.forEach((orientation) => {
              potentialSlotsData.forEach((slotData) => {
                // Для коридора пропускаем неправильные ориентации
                if (isCorridorModule && isStandardModule) {
                  // Для коридора: left/right требуют горизонтальную ориентацию, top/bottom - вертикальную
                  const isHorizontalOrientation = Math.abs(orientation.width - standardHorizontalWidth) < EPSILON;
                  const isVerticalOrientation = Math.abs(orientation.width - standardVerticalWidth) < EPSILON;

                  if ((slotData.side === "left" || slotData.side === "right") && !isHorizontalOrientation) {
                    return; // Пропускаем вертикальную ориентацию для left/right
                  }
                  if ((slotData.side === "top" || slotData.side === "bottom") && !isVerticalOrientation) {
                    return; // Пропускаем горизонтальную ориентацию для top/bottom
                  }
                }

                // Для коридорных модулей при размещении слева/справа модуль должен быть горизонтальным
                // Для коридорных модулей при размещении сверху/снизу модуль должен быть вертикальным
                let actualModuleWidth = orientation.width;
                let actualModuleHeight = orientation.height;
                let actualRot = slotData.rot;
                let actualDx = slotData.dx;
                let actualDy = slotData.dy;

                // Пересчитываем координаты для текущей ориентации
                if (slotData.side === "top" || slotData.side === "bottom") {
                  actualDx = (width - actualModuleWidth) / 2;
                  actualDy = slotData.side === "top" ? -actualModuleHeight : height;
                } else if (slotData.side === "left" || slotData.side === "right") {
                  actualDx = slotData.side === "left" ? -actualModuleWidth : width;
                  actualDy = (height - actualModuleHeight) / 2;
                }

                if (isCorridorModule && isStandardModule) {
                  // Для стандартного модуля при перемещении рядом с коридором
                  // размеры уже правильные (мы пропустили неправильные ориентации выше)
                  // Просто убеждаемся, что rotation = 0
                  actualRot = 0;
                } else if (isCorridorModule && (slotData.side === "left" || slotData.side === "right")) {
                  // Для нестандартных модулей при размещении слева/справа модуль должен быть горизонтальным

                  // Модуль должен быть горизонтальным - вместо поворота меняем размеры местами
                  actualRot = 0; // БЕЗ ПОВОРОТА!

                  // Меняем размеры местами чтобы модуль был горизонтальным
                  // newModuleWidth = 2.44 (короткая), newModuleHeight = 6.1 (длинная)
                  // Для горизонтального: width = 6.1, height = 2.44
                  actualModuleWidth = newModuleHeight;  // 6.1 - горизонтальная ширина
                  actualModuleHeight = newModuleWidth;  // 2.44 - вертикальная высота

                  if (slotData.side === "left") {
                    // Слева: модуль слева от коридора
                    // Коридор: x от 0 до width (2.44)
                    // Модуль горизонтальный 6.1 x 2.44, должен примыкать справа к x=0
                    // Левый край модуля: x = 0 - actualModuleWidth = -6.1
                    actualDx = -actualModuleWidth; // -6.1
                    actualDy = (height - actualModuleHeight) / 2; // Центрирование по высоте
                  } else { // right
                    // Справа: модуль справа от коридора  
                    // Коридор заканчивается на x=width (2.44)
                    // Модуль должен начинаться от x=width
                    actualDx = width; // 2.44
                    actualDy = (height - actualModuleHeight) / 2; // Центрирование по высоте
                  }

                }

                // Проверка совпадения сторон
                // Для коридора со стандартными модулями используем actualModuleWidth/Height
                if (isCorridorModule && isStandardModule) {
                  // Для коридора со стандартными модулями проверяем совпадение сторон с actualModuleWidth/Height
                  const existingModuleRelevantSideLength =
                    slotData.relevantSide === "width" ? width : height;
                  const newModuleRelevantSideLength =
                    slotData.relevantSide === "width" ? actualModuleWidth : actualModuleHeight;

                  if (
                    Math.abs(
                      existingModuleRelevantSideLength - newModuleRelevantSideLength,
                    ) > EPSILON
                  ) {
                    return;
                  }
                } else if (!isCorridorModule && isStandardModule) {
                  // Для не-коридора со стандартными модулями при перемещении проверяем совпадение сторон с actualModuleWidth/Height
                  const existingModuleRelevantSideLength =
                    slotData.relevantSide === "width" ? width : height;
                  const newModuleRelevantSideLength =
                    slotData.relevantSide === "width" ? actualModuleWidth : actualModuleHeight;

                  if (
                    Math.abs(
                      existingModuleRelevantSideLength - newModuleRelevantSideLength,
                    ) > EPSILON
                  ) {
                    return;
                  }
                } else {
                  // Для остальных случаев используем стандартную проверку
                  if (!isCorridorModule || (slotData.side !== "left" && slotData.side !== "right")) {
                    const existingModuleRelevantSideLength =
                      slotData.relevantSide === "width" ? width : height;
                    const newModuleRelevantSideLength =
                      slotData.relevantSide === "width" ? newModuleWidth : newModuleHeight;

                    if (
                      Math.abs(
                        existingModuleRelevantSideLength - newModuleRelevantSideLength,
                      ) > EPSILON
                    ) {
                      return;
                    }
                  }
                }

                // Преобразуем локальные координаты в мировые для всех модулей
                const rotatedOffset = rotatePoint(
                  { x: actualDx, y: actualDy },
                  { x: 0, y: 0 },
                  rotation,
                );

                const finalX = x + rotatedOffset.x;
                const finalY = y + rotatedOffset.y;
                const finalRotation = (rotation + actualRot) % 360;


                const slotObject = {
                  id: generateId("slot_"),
                  x: finalX,
                  y: finalY,
                  rotation: finalRotation,
                  width: actualModuleWidth,
                  height: actualModuleHeight,
                  anchorModuleId: module.id,
                  anchorSide: slotData.side,
                };


                // --- SMART GAP DETECTION ---
                // Проверяем, не оставляет ли этот слот "мертвую зону" (неиспользуемый зазор)
                const MIN_USABLE_GAP = 2.44; // Минимальная ширина модуля (2 клетки)
                const GAP_TOLERANCE = 0.05; // Допуск для сравнения float

                let leavesUnusableGap = false;

                // Находим ближайшее препятствие в направлении размещения
                if (slotData.side === "right") {
                  // Ищем модуль справа от слота
                  const slotRightEdge = slotObject.x + slotObject.width;

                  // Находим ближайший модуль справа, который перекрывается по Y
                  let nearestObstacleX = Infinity;

                  modulesOnThisFloor.forEach(m => {
                    if (m.id === module.id) return; // Пропускаем сам якорь

                    // Проверка перекрытия по Y (с допуском)
                    const yOverlap = Math.max(0, Math.min(slotObject.y + slotObject.height, m.y + m.height) - Math.max(slotObject.y, m.y));
                    if (yOverlap > GAP_TOLERANCE) {
                      // Если модуль справа
                      if (m.x > slotObject.x + GAP_TOLERANCE) {
                        nearestObstacleX = Math.min(nearestObstacleX, m.x);
                      }
                    }
                  });

                  if (nearestObstacleX !== Infinity) {
                    const gapSize = nearestObstacleX - slotRightEdge;
                    // Если зазор есть, но он меньше минимального модуля -> БЛОКИРУЕМ
                    if (gapSize > GAP_TOLERANCE && gapSize < MIN_USABLE_GAP - GAP_TOLERANCE) {
                      leavesUnusableGap = true;
                    }
                  }

                } else if (slotData.side === "left") {
                  // Ищем модуль слева от слота
                  const slotLeftEdge = slotObject.x;

                  let nearestObstacleRightEdge = -Infinity;

                  modulesOnThisFloor.forEach(m => {
                    if (m.id === module.id) return;

                    const yOverlap = Math.max(0, Math.min(slotObject.y + slotObject.height, m.y + m.height) - Math.max(slotObject.y, m.y));
                    if (yOverlap > GAP_TOLERANCE) {
                      // Если модуль слева
                      if (m.x + m.width < slotObject.x + slotObject.width - GAP_TOLERANCE) {
                        nearestObstacleRightEdge = Math.max(nearestObstacleRightEdge, m.x + m.width);
                      }
                    }
                  });

                  if (nearestObstacleRightEdge !== -Infinity) {
                    const gapSize = slotLeftEdge - nearestObstacleRightEdge;
                    if (gapSize > GAP_TOLERANCE && gapSize < MIN_USABLE_GAP - GAP_TOLERANCE) {
                      leavesUnusableGap = true;
                    }
                  }
                }
                // TODO: Add Top/Bottom logic if needed (currently prioritizing horizontal flow)

                if (leavesUnusableGap) return;
                // ---------------------------

                // Проверяем коллизии для ВСЕХ слотов (включая коридорные)
                const hasCollision = modulesOnThisFloor.some((existingModule) =>
                  existingModule.id !== module.id && checkOverlapWithRotation(slotObject, existingModule),
                );
                if (hasCollision) return;

                // Дополнительная проверка: убеждаемся, что размеры слота соответствуют размерам модуля
                // (для коридорных модулей размеры могут быть переставлены)
                let slotWidthMatch, slotHeightMatch;

                if (isStandardModule) {
                  // Для стандартных модулей при перемещении проверяем соответствие стандартным размерам
                  slotWidthMatch = Math.abs(slotObject.width - standardVerticalWidth) < EPSILON ||
                    Math.abs(slotObject.width - standardHorizontalWidth) < EPSILON;
                  slotHeightMatch = Math.abs(slotObject.height - standardVerticalHeight) < EPSILON ||
                    Math.abs(slotObject.height - standardHorizontalHeight) < EPSILON;
                } else {
                  // Для остальных модулей проверяем соответствие newModuleWidth/Height или их перевернутым значениям
                  slotWidthMatch = Math.abs(slotObject.width - newModuleWidth) < EPSILON ||
                    Math.abs(slotObject.width - newModuleHeight) < EPSILON;
                  slotHeightMatch = Math.abs(slotObject.height - newModuleHeight) < EPSILON ||
                    Math.abs(slotObject.height - newModuleWidth) < EPSILON;
                }

                if (!slotWidthMatch || !slotHeightMatch) {
                  // Размеры не совпадают - пропускаем этот слот
                  return;
                }

                const slotKey = `${slotObject.x.toFixed(2)}-${slotObject.y.toFixed(2)}-${slotObject.rotation}`;
                if (!slotKeySet.has(slotKey)) {
                  newSlots.push(slotObject);
                  slotKeySet.add(slotKey);
                }
              });
            });
          });
        }
      }

      // Финальная валидация слотов - проверяем, что модуль действительно может быть размещен
      const validatedSlots = newSlots.filter((slot) => {
        // Проверяем размеры - слот должен соответствовать размерам модуля (с учетом возможного поворота)
        // Модуль может быть размещен если размеры слота совпадают с размерами модуля
        // или если размеры совпадают при повороте на 90 градусов

        // Для стандартных модулей при перемещении проверяем соответствие стандартным размерам
        const isStandardModuleSlot = originalModuleId !== null && newModuleConfig &&
          ((newModuleConfig.cellsWide === 2 && newModuleConfig.cellsLong === 5) ||
            (newModuleConfig.cellsWide === 5 && newModuleConfig.cellsLong === 2));

        let moduleArea, directMatch, rotatedMatch;

        if (isStandardModuleSlot) {
          // Для стандартных модулей проверяем соответствие стандартным размерам
          const standardVerticalArea = standardVerticalWidth * standardVerticalHeight;
          const standardHorizontalArea = standardHorizontalWidth * standardHorizontalHeight;
          const slotArea = slot.width * slot.height;

          // Площадь должна соответствовать стандартной (вертикальной или горизонтальной)
          if (Math.abs(slotArea - standardVerticalArea) > EPSILON &&
            Math.abs(slotArea - standardHorizontalArea) > EPSILON) {
            return false;
          }

          // Размеры должны совпадать со стандартными (либо вертикальными, либо горизонтальными)
          directMatch = (Math.abs(slot.width - standardVerticalWidth) < EPSILON &&
            Math.abs(slot.height - standardVerticalHeight) < EPSILON) ||
            (Math.abs(slot.width - standardHorizontalWidth) < EPSILON &&
              Math.abs(slot.height - standardHorizontalHeight) < EPSILON);
          rotatedMatch = (Math.abs(slot.width - standardVerticalHeight) < EPSILON &&
            Math.abs(slot.height - standardVerticalWidth) < EPSILON) ||
            (Math.abs(slot.width - standardHorizontalHeight) < EPSILON &&
              Math.abs(slot.height - standardHorizontalWidth) < EPSILON);
        } else {
          // Для остальных модулей используем стандартную проверку
          const slotArea = slot.width * slot.height;
          const moduleArea = newModuleWidth * newModuleHeight;

          // Площади должны совпадать
          if (Math.abs(slotArea - moduleArea) > EPSILON) {
            return false;
          }

          // Размеры должны совпадать (либо прямо, либо при повороте)
          directMatch = (Math.abs(slot.width - newModuleWidth) < EPSILON &&
            Math.abs(slot.height - newModuleHeight) < EPSILON);
          rotatedMatch = (Math.abs(slot.width - newModuleHeight) < EPSILON &&
            Math.abs(slot.height - newModuleWidth) < EPSILON);
        }

        if (!directMatch && !rotatedMatch) {
          return false;
        }

        // Проверяем поддержку модуля (для этажей выше первого)
        if (currentFloorIndex > 0) {
          const ghostModulesFromBelow = ghostsFromBelow.filter(
            (o) => o.type === OBJECT_TYPES.MODULE,
          );
          if (!isModuleFullySupported(slot, ghostModulesFromBelow)) {
            return false;
          }
        }

        // Проверяем коллизии с существующими модулями
        const hasCollision = modulesOnThisFloor.some((existingModule) =>
          checkOverlapWithRotation(slot, existingModule),
        );
        if (hasCollision) {
          return false;
        }

        return true;
      });

      // Финальный лог для коридорных модулей
      const corridorSlots = validatedSlots.filter(slot => {
        const anchorModule = modulesOnThisFloor.find(m => m.id === slot.anchorModuleId);
        return anchorModule && anchorModule.subtype === 'corridor' && (slot.anchorSide === 'left' || slot.anchorSide === 'right');
      });


      if (validatedSlots.length > 0) {
        setPlacementSlots(validatedSlots);
        setIsPlacementModeActive(true);
      } else {
        if (
          draggedTemplate ||
          (modulesOnThisFloor.length > 0 && !draggedTemplate)
        ) {
          toast.error(t("toasts.noPlacementSlotsAvailable"));
        }
        setPlacementSlots([]);
        setIsPlacementModeActive(false);
      }
    },
    [
      objectsOnCurrentFloor,
      currentFloorIndex,
      ghostsFromBelow,
      setPlacementSlots,
      setIsPlacementModeActive,
      isModuleFullySupported,
      t,
      canShowInitialModuleButton,
      initialModulePlacementCoords,
      exitPlacementMode,
      originalModuleId,
      moduleToMove,
    ],
  );

  // Автоматически пересчитываем слоты после удаления модуля для перемещения
  useEffect(() => {
    if (moduleToMove && originalModuleId) {
      // Проверяем, что модуль действительно удален (его нет в objectsOnCurrentFloor)
      const moduleStillExists = objectsOnCurrentFloor.some(
        (o) => o.type === OBJECT_TYPES.MODULE && o.id === originalModuleId
      );

      // Если модуль удален, пересчитываем слоты
      if (!moduleStillExists) {
        calculateAndSetPlacementSlots(moduleToMove);
      }
    }
  }, [objectsOnCurrentFloor, moduleToMove, originalModuleId, calculateAndSetPlacementSlots]);

  // Отслеживание движения мыши для обновления позиции ghost модуля
  // Отслеживание движения мыши для обновления позиции ghost модуля
  useEffect(() => {
    if (!moduleToMove) return;

    const handleMouseMove = (e) => {
      const worldPos = screenToWorld(e.clientX, e.clientY);
      // Снаппим к сетке
      const snappedX = Math.round(worldPos.x / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;
      const snappedY = Math.round(worldPos.y / GRID_CELL_SIZE_M) * GRID_CELL_SIZE_M;
      setGhostModulePosition({ x: snappedX, y: snappedY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [moduleToMove, screenToWorld]);

  // Обработчик клика для размещения модуля, когда ghost-модуль над слотом
  useEffect(() => {
    if (!moduleToMove || !ghostModulePosition || !savedModuleForMove) return;

    const handleClick = (e) => {
      // Пропускаем клики на элементы UI (панели, кнопки и т.д.)
      if (e.target.closest('.sidebar') || e.target.closest('button') || e.target.closest('[role="button"]')) {
        return;
      }

      // Пропускаем клики на сами слоты (они обрабатываются отдельно)
      if (e.target.closest('g[id*="placement-slots"]') || e.target.closest('[data-slot-id]')) {
        return;
      }

      // Ищем слот, над которым находится ghost-модуль
      const ghostModule = {
        ...savedModuleForMove,
        x: ghostModulePosition.x,
        y: ghostModulePosition.y,
        rotation: savedModuleForMove.rotation || 0,
        width: (moduleToMove.config?.cellsWide || savedModuleForMove.cellsWide) * GRID_CELL_SIZE_M,
        height: (moduleToMove.config?.cellsLong || savedModuleForMove.cellsLong) * GRID_CELL_SIZE_M,
      };

      const overlappingSlot = placementSlots.find((slot) => {
        const slotModule = {
          x: slot.x,
          y: slot.y,
          rotation: slot.rotation || 0,
          width: slot.width,
          height: slot.height,
        };
        return checkOverlapWithRotation(ghostModule, slotModule);
      });

      if (overlappingSlot && moduleToMove) {
        const slotCellsWide = Math.round(overlappingSlot.width / GRID_CELL_SIZE_M);
        const slotCellsLong = Math.round(overlappingSlot.height / GRID_CELL_SIZE_M);

        const moduleConfig = {
          ...moduleToMove.config,
          posX: overlappingSlot.x,
          posY: overlappingSlot.y,
          rotation: overlappingSlot.rotation,
          cellsWide: slotCellsWide,
          cellsLong: slotCellsLong,
        };

        dispatch({ type: "ADD_MODULE", payload: { moduleConfig } });

        // Очищаем состояние перемещения
        setModuleToMove(null);
        setOriginalModuleId(null);
        setSavedModuleForMove(null);
        setGhostModulePosition(null);
        exitPlacementMode();
      }
    };

    window.addEventListener('click', handleClick, true);
    return () => {
      window.removeEventListener('click', handleClick, true);
    };
  }, [moduleToMove, ghostModulePosition, savedModuleForMove, placementSlots, dispatch, exitPlacementMode]);

  // Функция для расчета слотов размещения каркасов на сетке
  const calculateFramePlacementSlots = useCallback((frameConfig) => {
    if (!frameConfig) {
      setPlacementSlots([]);
      setIsPlacementModeActive(false);
      return;
    }

    const frameWidth = frameConfig.sandwichCountWidth * FRAMELESS_GRID_CELL_SIZE_M;
    const frameHeight = frameConfig.sandwichCountLength * FRAMELESS_GRID_CELL_SIZE_M;

    // Создаем один статичный слот в центре сетки (0, 0)
    const newSlot = {
      id: generateId("frame-slot-"),
      x: 0,
      y: 0,
      rotation: 0,
      width: frameWidth,
      height: frameHeight,
    };

    setPlacementSlots([newSlot]);
    setIsPlacementModeActive(true);
    // Устанавливаем позицию ghost каркаса в центр слота
    setGhostModulePosition({ x: 0, y: 0 });
  }, [setPlacementSlots, setIsPlacementModeActive]);

  const handleDragStart = (event) => {
    if (event.active.data.current?.template) {
      const template = event.active.data.current.template;
      setActiveDragTemplate(template);
      calculateAndSetPlacementSlots(template);
    } else if (event.active.data.current?.frameConfig || event.active.data.current?.type === 'FRAME_TEMPLATE') {
      const frameConfig = event.active.data.current?.frameConfig;
      if (frameConfig) {
        setActiveDragFrame(frameConfig);
        calculateFramePlacementSlots(frameConfig);
      }
    }
  };

  // Функция для преобразования модуля в шаблон с сохранением всех элементов
  const moduleToTemplate = useCallback((module, floorObjects = []) => {
    if (!module || module.type !== OBJECT_TYPES.MODULE) return null;

    // Получаем все стыки из объектов на этаже
    const allJoints = floorObjects.filter(
      (o) => o.type === OBJECT_TYPES.DOCKING_JOINT,
    );

    // Собираем все элементы из wallSegments в формат predefinedElements
    const predefinedElements = [];
    if (module.wallSegments) {
      Object.entries(module.wallSegments).forEach(([segmentKey, segment]) => {
        if (segment.elements && Array.isArray(segment.elements)) {
          segment.elements.forEach((element) => {
            // Пропускаем только копии элементов (isAdjacentCopy)
            // Сохраняем все элементы, даже если они на стыках
            if (element.isAdjacentCopy) {
              return;
            }

            // Создаем элемент с сохранением всех свойств
            const elementData = {
              type: element.type,
              segmentKey: segmentKey,
              properties: {
                positionOnSegment: element.positionOnSegment || 0.5,
              },
            };

            // Сохраняем все свойства элемента
            // Копируем все свойства, кроме служебных
            const propertiesToSkip = ['id', 'isAdjacentCopy'];
            Object.keys(element).forEach(key => {
              if (!propertiesToSkip.includes(key) && key !== 'type') {
                elementData.properties[key] = element[key];
              }
            });

            predefinedElements.push(elementData);
          });
        }
      });
    }

    // Создаем Set для отслеживания уже сохраненных элементов по их позиции и типу
    const savedElementsSet = new Set();
    predefinedElements.forEach(el => {
      const key = `${el.segmentKey}-${el.type}-${el.properties.positionOnSegment}`;
      savedElementsSet.add(key);
    });

    // Также сохраняем элементы из стыков, связанных с этим модулем
    const moduleJoints = allJoints.filter(joint =>
      joint.moduleA_id === module.id || joint.moduleB_id === module.id
    );

    moduleJoints.forEach(joint => {
      if (joint.elements && Array.isArray(joint.elements)) {
        joint.elements.forEach((element) => {
          // Сохраняем элементы из стыков как predefinedElements
          // Найдем соответствующий segmentKey для этого стыка
          // Для стыков элементы размещаются на сегментах, которые связаны со стыком
          const segmentKey = Object.keys(module.wallSegments || {}).find(key => {
            const segment = module.wallSegments[key];
            return segment.jointId === joint.id;
          });

          if (segmentKey) {
            // Проверяем, не сохранен ли уже этот элемент
            const elementKey = `${segmentKey}-${element.type}-${element.positionOnSegment || 0.5}`;
            if (savedElementsSet.has(elementKey)) {
              return; // Элемент уже сохранен
            }

            const elementData = {
              type: element.type,
              segmentKey: segmentKey,
              properties: {
                positionOnSegment: element.positionOnSegment || 0.5,
              },
            };

            // Сохраняем все свойства элемента
            // Копируем все свойства, кроме служебных
            const propertiesToSkip = ['id', 'isAdjacentCopy'];
            Object.keys(element).forEach(key => {
              if (!propertiesToSkip.includes(key) && key !== 'type') {
                elementData.properties[key] = element[key];
              }
            });

            predefinedElements.push(elementData);
            savedElementsSet.add(elementKey);
          }
        });
      }
    });

    // Создаем конфигурацию шаблона
    const config = {
      id: module.templateId || module.id,
      cellsWide: module.cellsWide,
      cellsLong: module.cellsLong,
      subtype: module.subtype,
      predefinedElements: predefinedElements,
      freeElements: module.freeElements ? JSON.parse(JSON.stringify(module.freeElements)) : [],
      // Сохраняем wallSegments для восстановления структуры
      wallSegments: module.wallSegments ? JSON.parse(JSON.stringify(module.wallSegments)) : {},
    };

    return {
      id: `move_template_${module.id}`,
      name: `Перемещаемый модуль`,
      config: config,
    };
  }, []);

  // Функция для начала перемещения модуля
  const startModuleMove = useCallback((module) => {
    const template = moduleToTemplate(module, objectsOnCurrentFloor);
    if (!template) {
      toast.error("Не вдалося підготувати модуль до переміщення");
      return;
    }

    // Сохраняем полный модуль для возможного восстановления
    const moduleCopy = JSON.parse(JSON.stringify(module));
    setSavedModuleForMove(moduleCopy);
    setModuleToMove(template);
    setOriginalModuleId(module.id);

    // Устанавливаем начальную позицию ghost модуля (текущая позиция модуля)
    setGhostModulePosition({ x: module.x, y: module.y });

    // Устанавливаем выбранный объект и удаляем модуль (он станет ghost)
    dispatch({ type: "SET_SELECTED_OBJECT_ID", payload: module.id });
    dispatch({ type: "DELETE_SELECTED_OBJECT" });
    dispatch({ type: "SET_SELECTED_OBJECT_ID", payload: null });

    // Входим в режим размещения
    calculateAndSetPlacementSlots(template);
  }, [moduleToTemplate, dispatch, calculateAndSetPlacementSlots, objectsOnCurrentFloor]);

  const handleDragEnd = (event) => {
    const { over } = event;
    const droppedTemplate = activeDragTemplate || moduleToMove;

    setActiveDragTemplate(null);

    // Если это перемещение модуля, обрабатываем отдельно
    if (moduleToMove && originalModuleId) {
      exitPlacementMode();

      if (over && over.data.current?.slot && droppedTemplate) {
        const slot = over.data.current.slot;
        const slotCellsWide = Math.round(slot.width / GRID_CELL_SIZE_M);
        const slotCellsLong = Math.round(slot.height / GRID_CELL_SIZE_M);

        // Подсчитываем элементы в сохраненном модуле
        let savedElementsCount = 0;
        if (savedModuleForMove && savedModuleForMove.wallSegments) {
          for (const segment of Object.values(savedModuleForMove.wallSegments)) {
            if (segment.elements && Array.isArray(segment.elements)) {
              savedElementsCount += segment.elements.length;
            }
          }
        }


        const moduleConfig = {
          ...droppedTemplate.config,
          posX: slot.x,
          posY: slot.y,
          rotation: slot.rotation,
          cellsWide: slotCellsWide,
          cellsLong: slotCellsLong,
          // Передаем старый модуль для сохранения элементов при повороте
          oldModule: savedModuleForMove && savedModuleForMove.wallSegments ? {
            wallSegments: JSON.parse(JSON.stringify(savedModuleForMove.wallSegments)), // Глубокая копия
            rotation: savedModuleForMove.rotation || 0,
            cellsWide: savedModuleForMove.cellsWide,
            cellsLong: savedModuleForMove.cellsLong,
          } : null,
        };

        // Добавляем модуль в новое место
        dispatch({ type: "ADD_MODULE", payload: { moduleConfig } });

        // Подтверждаем удаление старого модуля (он уже был удален временно)
        // Очищаем состояние перемещения
        setModuleToMove(null);
        setOriginalModuleId(null);
        setSavedModuleForMove(null);
        setGhostModulePosition(null);
      } else {
        // Отмена перемещения - очищаем состояние
        // Модуль уже удален, пользователь может восстановить его через историю
        toast.info("Переміщення скасовано. Можна відновити модуль через історію.");
        setModuleToMove(null);
        setOriginalModuleId(null);
        setSavedModuleForMove(null);
        setGhostModulePosition(null);
      }
      return;
    }

    // Обычная обработка drag and drop для новых модулей
    exitPlacementMode();

    if (over && over.data.current?.slot && droppedTemplate) {
      const slot = over.data.current.slot;

      // Используем размеры из слота, если они есть (для горизонтальных коридорных слотов)
      // Слот может иметь размеры, отличные от шаблона (например, для коридоров)
      const slotCellsWide = Math.round(slot.width / GRID_CELL_SIZE_M);
      const slotCellsLong = Math.round(slot.height / GRID_CELL_SIZE_M);

      const moduleConfig = {
        ...droppedTemplate.config,
        posX: slot.x,
        posY: slot.y,
        rotation: slot.rotation,
        // Если размеры слота отличаются от шаблона, используем размеры слота
        cellsWide: slotCellsWide,
        cellsLong: slotCellsLong,
      };

      dispatch({ type: "ADD_MODULE", payload: { moduleConfig } });
    } else if (activeDragFrame) {
      if (over && over.data.current?.slot) {
        // Handle frame drop on slot (like modules)
        const slot = over.data.current.slot;

        const frameConfig = {
          ...activeDragFrame,
          posX: slot.x,
          posY: slot.y,
          rotation: slot.rotation || 0,
        };

        dispatch({ type: "ADD_FRAME_STRUCTURE", payload: { frameConfig } });
        setActiveDragFrame(null);
        exitPlacementMode();
        setGhostModulePosition(null);
      } else {
        // Frame was dropped but not on a slot - just clear state
        setActiveDragFrame(null);
        exitPlacementMode();
        setGhostModulePosition(null);
      }
    }
  };

  useKeyboardShortcuts({
    mainContainerRef,
    deleteSelectedObject: primarySelectedObject
      ? () => dispatch({ type: "DELETE_SELECTED_OBJECT" })
      : null,
    deselectAll: () => {
      dispatch({ type: "SET_SELECTED_OBJECT_ID", payload: null });
      if (state.drawingWall) {
        dispatch({ type: "CANCEL_DRAWING_WALL" });
      }
      setElementTypeToPlace(null);
      // Если есть модуль в процессе перемещения, отменяем перемещение
      if (moduleToMove && originalModuleId) {
        // Очищаем состояние перемещения
        setModuleToMove(null);
        setOriginalModuleId(null);
        setSavedModuleForMove(null);
        setGhostModulePosition(null);
        toast.info("Переміщення скасовано. Можна відновити модуль через історію.");
      }
      exitPlacementMode();
      closeContextMenu();
      setIsHistoryPanelOpen(false);
      setIsMobileElementsOpen(false);
      setIsMobilePropertiesOpen(false);
    },
  });

  useEffect(() => {
    mainContainerRef.current?.focus();
  }, []);

  const panelVariants = {
    hidden: (isRightPanel = false) => ({
      opacity: 0,
      x: isRightPanel ? 50 : -50,
    }),
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.2,
      },
    },
    exit: (isRightPanel = false) => ({
      opacity: 0,
      x: isRightPanel ? 50 : -50,
      transition: { duration: 0.15 },
    }),
  };

  const allJoints = useMemo(
    () =>
      objectsOnCurrentFloor.filter(
        (o) => o.type === OBJECT_TYPES.DOCKING_JOINT,
      ),
    [objectsOnCurrentFloor],
  );

  const framelessPartitions = useMemo(
    () =>
      objectsOnCurrentFloor.filter(
        (o) => o.type === OBJECT_TYPES.FRAMELESS_PARTITION,
      ),
    [objectsOnCurrentFloor],
  );

  const selectableTemplates = useMemo(() => {
    return availableTemplates.filter((template) => {
      if (
        !template ||
        !template.config ||
        typeof template.config.id !== "string"
      ) {
        return false;
      }
      // Сходи 2-го поверху завжди приховані - вони ставляться автоматично
      if (template.config.id.includes("floor2")) {
        return false;
      }
      return true;
    });
  }, [availableTemplates]);

  const isFramePlaced = useMemo(() => {
    return objectsOnCurrentFloor.some(obj => obj.type === OBJECT_TYPES.FRAME_STRUCTURE);
  }, [objectsOnCurrentFloor]);

  const handleSetActiveMode = (mode) => {
    if (state.activeMode !== mode) {
      if (
        objectsOnCurrentFloor.length > 0 &&
        window.confirm(t("warnings.switchModeConfirm"))
      ) {
        // Сбрасываем выбранный элемент и модуль при переключении режимов
        setElementTypeToPlace(null);
        dispatch({ type: "SET_SELECTED_OBJECT_ID", payload: null });
        dispatch({ type: "SET_ACTIVE_MODE", payload: mode });
      } else if (objectsOnCurrentFloor.length === 0) {
        // Сбрасываем выбранный элемент и модуль при переключении режимов
        setElementTypeToPlace(null);
        dispatch({ type: "SET_SELECTED_OBJECT_ID", payload: null });
        dispatch({ type: "SET_ACTIVE_MODE", payload: mode });
      }
    }
  };

  const statusBarText = useMemo(() => {
    if (
      state.activeMode === MODES.FRAMELESS &&
      elementTypeToPlace === OBJECT_TYPES.FRAMELESS_WALL_TOOL
    ) {
      return "drawing_instruction";
    }
    return elementTypeToPlace
      ? `${t("statusBar.placing", { elementType: elementTypeToPlace.replace(/_/g, " ") })}`
      : primarySelectedObject?.label ||
      primarySelectedObject?.type?.replace("_", " ") ||
      "";
  }, [state.activeMode, elementTypeToPlace, primarySelectedObject, t]);

  let overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    pointerEvents: "none",
  };
  if (activeDragTemplate && dragOverlayCoords) {
    const x = dragOverlayCoords.x - OVERLAY_WIDTH / 2;
    const y = dragOverlayCoords.y - OVERLAY_HEIGHT / 2;
    overlayStyle.transform = `translate(${x}px, ${y}px)`;
  }

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      collisionDetection={customCollisionDetection}
    >
      <DragMonitor setDragOverlayCoords={setDragOverlayCoords} />

      <div
        ref={mainContainerRef}
        className="w-full h-full flex flex-col select-none outline-none bg-light-bg"
        tabIndex={-1}
      >
        <input
          type="file"
          ref={loadFileInputRef}
          onChange={handleLoadProject}
          accept=".json"
          style={{ display: "none" }}
        />

        <div className="hidden lg:block">
          <ConfiguratorToolbar
            activeMode={state.activeMode}
            setActiveMode={handleSetActiveMode}
            onClearAll={() => dispatch({ type: "CLEAR_CURRENT_FLOOR" })}
            floors={state.floors}
            currentFloorId={state.currentFloorId}
            addFloor={() => dispatch({ type: "ADD_FLOOR" })}
            deleteFloor={(id) =>
              dispatch({ type: "DELETE_FLOOR", payload: id })
            }
            switchToFloor={(id) =>
              dispatch({ type: "SWITCH_TO_FLOOR", payload: id })
            }
            onOpenEnquiryModal={handleOpenEnquiryModal}
            isAiPanelOpen={isAiPanelOpen}
            setIsAiPanelOpen={setIsAiPanelOpen}
            totalCost={totalCost}
            aiQuery={aiQuery}
            setAiQuery={setAiQuery}
            handleAiSubmit={handleAiSubmit}
            isAiLoading={isAiLoading}
            onOpenInstructionModal={() => {
              console.log("Opening instruction modal...");
              setIsInstructionModalOpen(true);
            }}
          />
        </div>

        <div className="flex flex-col lg:flex-row flex-grow overflow-hidden relative">
          <AnimatePresence>
            {isHistoryPanelOpen && (
              <HistoryPanel
                onClose={() => setIsHistoryPanelOpen(false)}
                onLoad={handleLoadFromHistory}
                onDelete={handleDeleteFromHistory}
              />
            )}
          </AnimatePresence>

          <div className="hidden lg:flex h-full flex-shrink-0 z-10">
            <AnimatePresence>
              {state.activeMode === MODES.MODULAR ? (
                <motion.div
                  key="modular-sidebar"
                  custom={false}
                  variants={panelVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="flex-shrink-0 z-10 h-full"
                >
                  <ModularSidebar
                    templates={selectableTemplates}
                    isCanvasEmpty={objectsOnCurrentFloor.length === 0}
                    onSelectElementType={(type) =>
                      setElementTypeToPlace((prev) =>
                        prev === type ? null : type,
                      )
                    }
                    selectedElementType={elementTypeToPlace}
                    activeMode={state.activeMode}
                  />
                </motion.div>
              ) : state.activeMode === MODES.FRAME ? (
                <motion.div
                  key="frame-sidebar"
                  custom={false}
                  variants={panelVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="flex-shrink-0 z-10 h-full"
                >
                  <FrameSelectionPanel
                    onClose={() => { }} // Optional: handle close if needed
                    isFramePlaced={isFramePlaced}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="element-panel"
                  custom={false}
                  variants={panelVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="flex-shrink-0 z-10 h-full w-[480px]"
                >
                  <ElementSelectionPanel
                    onSelectElementType={(type) =>
                      setElementTypeToPlace((prev) =>
                        prev === type ? null : type,
                      )
                    }
                    selectedElementType={elementTypeToPlace}
                    activeMode={state.activeMode}
                    framelessElementsUnlocked={state.framelessElementsUnlocked}
                    hasRooms={hasRooms}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex-grow flex items-center justify-center p-0 lg:p-4 bg-light-bg relative min-w-0 h-full pb-20 lg:pb-0">
            <div className="flex flex-row items-center justify-center gap-4 w-full h-full">
              <div className="relative bg-card-bg-light shadow-xl w-full h-full lg:max-w-[1920px] lg:max-h-[1080px] overflow-hidden lg:rounded-md border-gray-300 flex-shrink">
                <SvgCanvas
                  svgRef={svgRef}
                  viewTransform={viewTransform}
                  setViewTransform={setViewTransform}
                  screenToWorld={screenToWorld}
                  mainContainerRef={mainContainerRef}
                  updateModulePosition={(id, x, y) =>
                    dispatch({
                      type: "UPDATE_MODULE_POSITION",
                      payload: { id, x, y },
                    })
                  }
                  snapAndFinalizeModulePosition={(id, x, y, rot) =>
                    dispatch({
                      type: "FINALIZE_MODULE_MOVE",
                      payload: {
                        moduleId: id,
                        originalX: x,
                        originalY: y,
                        originalRotation: rot,
                      },
                    })
                  }
                  objects={objectsToRender}
                  allJoints={allJoints}
                  framelessPartitions={framelessPartitions}
                  ghostObjects={ghostsFromBelow}
                  activeMode={state.activeMode}
                  selectedObjectId={state.selectedObjectId}
                  setSelectedObjectId={(id) =>
                    dispatch({ type: "SET_SELECTED_OBJECT_ID", payload: id })
                  }
                  canAddInitialModule={canShowInitialModuleButton}
                  onAddModule={() => { }}
                  initialModulePlacementCoords={initialModulePlacementCoords}
                  onToggleWallSegment={(moduleId, cellX, cellY, orientation) => {
                    dispatch({
                      type: "TOGGLE_INTERNAL_WALL_SEGMENT",
                      payload: { moduleId, cellX, cellY, orientation },
                    });
                  }}
                  primarySelectedObject={primarySelectedObject}
                  elementTypeToPlace={elementTypeToPlace}
                  onWallSegmentClick={handleWallSegmentClickForPlacement}
                  isPlacementModeActive={isPlacementModeActive}
                  placementSlots={isPlacementModeActive ? placementSlots : []}
                  onPlacementSlotClick={(slot) => {
                    // Если есть модуль в процессе перемещения, размещаем его
                    if (moduleToMove && slot) {
                      const slotCellsWide = Math.round(slot.width / GRID_CELL_SIZE_M);
                      const slotCellsLong = Math.round(slot.height / GRID_CELL_SIZE_M);

                      // Подсчитываем элементы в сохраненном модуле
                      let savedElementsCount = 0;
                      if (savedModuleForMove && savedModuleForMove.wallSegments) {
                        for (const segment of Object.values(savedModuleForMove.wallSegments)) {
                          if (segment.elements && Array.isArray(segment.elements)) {
                            savedElementsCount += segment.elements.length;
                          }
                        }
                      }


                      const moduleConfig = {
                        ...moduleToMove.config,
                        posX: slot.x,
                        posY: slot.y,
                        rotation: slot.rotation,
                        cellsWide: slotCellsWide,
                        cellsLong: slotCellsLong,
                        // Передаем старый модуль для сохранения элементов при повороте
                        oldModule: savedModuleForMove && savedModuleForMove.wallSegments ? {
                          wallSegments: JSON.parse(JSON.stringify(savedModuleForMove.wallSegments)), // Глубокая копия
                          rotation: savedModuleForMove.rotation || 0,
                          cellsWide: savedModuleForMove.cellsWide,
                          cellsLong: savedModuleForMove.cellsLong,
                        } : null,
                      };

                      dispatch({ type: "ADD_MODULE", payload: { moduleConfig } });

                      // Очищаем состояние перемещения
                      setModuleToMove(null);
                      setOriginalModuleId(null);
                      setSavedModuleForMove(null);
                      setGhostModulePosition(null);
                      exitPlacementMode();
                    }
                  }}
                  currentFloorIndex={currentFloorIndex}
                  onContextMenu={handleContextMenuAction}
                  drawingWall={state.drawingWall}
                  dispatch={dispatch}
                  moduleToMove={moduleToMove}
                  ghostModulePosition={ghostModulePosition}
                  savedModuleForMove={savedModuleForMove}
                  internalWallSlots={internalWallSlots}
                  onInternalWallSlotClick={(slot) => {
                    // Слот теперь содержит линию (startPoint и endPoint)
                    // Создаем внутреннюю стену вдоль этой линии
                    if (slot.startPoint && slot.endPoint) {
                      dispatch({
                        type: "CREATE_FRAMELESS_INTERNAL_WALL",
                        payload: {
                          startPoint: slot.startPoint,
                          endPoint: slot.endPoint,
                          parentRoomId: `room-${slot.roomIndex}`,
                          parentWallId: null,
                          segmentId: null
                        }
                      });

                      setElementTypeToPlace(null);
                    }
                  }}
                  framelessLastWallId={state.framelessLastWallId}
                  activeDragFrame={activeDragFrame}
                />
              </div>
              <div className="hidden flex-shrink-0 h-full items-end">
                <CostPanel
                  totalCost={totalCost}
                  costBreakdown={costBreakdown}
                  projectState={state}
                  dispatch={dispatch}
                  svgRef={svgRef}
                  setViewTransform={setViewTransform}
                  viewTransform={viewTransform}
                />
              </div>
            </div>
          </div>

          {/* Мобильная версия CostPanel - внизу экрана */}
          <div className="hidden fixed bottom-4 right-4 left-4 z-20">
            <CostPanel
              totalCost={totalCost}
              costBreakdown={costBreakdown}
              projectState={state}
              dispatch={dispatch}
              svgRef={svgRef}
              setViewTransform={setViewTransform}
              viewTransform={viewTransform}
            />
          </div>

          <div className="hidden lg:flex h-full flex-shrink-0">
            <AnimatePresence>
              {primarySelectedObject && (
                <PropertiesPanel
                  primarySelectedObject={primarySelectedObject}
                  dispatch={dispatch}
                  state={state}
                  jointObject={
                    primarySelectedObject.parentJointId
                      ? getObjectById(
                        primarySelectedObject.parentJointId,
                        state.currentFloorId,
                        state.projectObjects,
                      )
                      : null
                  }
                  onStartModuleMove={startModuleMove}
                />
              )}
            </AnimatePresence>
          </div>

          <AnimatePresence>
            {isMobileElementsOpen && (
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", stiffness: 400, damping: 40 }}
                className="fixed bottom-0 left-0 right-0 h-3/4 bg-white shadow-top rounded-t-2xl z-40 lg:hidden flex flex-col"
              >
                <div className="flex justify-end p-2 border-b border-gray-200 flex-shrink-0">
                  <button
                    onClick={() => setIsMobileElementsOpen(false)}
                    className="p-2 rounded-full hover:bg-gray-100"
                  >
                    <XMarkIcon className="w-6 h-6 text-gray-600" />
                  </button>
                </div>
                <div className="flex-1 overflow-hidden">
                  <ElementSelectionPanel
                    onSelectElementType={(type) => {
                      setElementTypeToPlace((prev) =>
                        prev === type ? null : type,
                      );
                      setIsMobileElementsOpen(false);
                    }}
                    selectedElementType={elementTypeToPlace}
                    activeMode={state.activeMode}
                    hasRooms={hasRooms}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {isMobilePropertiesOpen && primarySelectedObject && (
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", stiffness: 400, damping: 40 }}
                className="fixed top-0 right-0 h-full w-full max-w-xs bg-white shadow-2xl z-40 lg:hidden"
              >
                <div className="flex justify-end p-2 border-b border-gray-200">
                  <button
                    onClick={() => setIsMobilePropertiesOpen(false)}
                    className="p-2 rounded-full hover:bg-gray-100"
                  >
                    <XMarkIcon className="w-6 h-6 text-gray-600" />
                  </button>
                </div>
                <PropertiesPanel
                  primarySelectedObject={primarySelectedObject}
                  dispatch={dispatch}
                  state={state}
                  jointObject={
                    primarySelectedObject.parentJointId
                      ? getObjectById(
                        primarySelectedObject.parentJointId,
                        state.currentFloorId,
                        state.projectObjects,
                      )
                      : null
                  }
                  onStartModuleMove={startModuleMove}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="lg:hidden">
          <MobileToolbar
            onToggleElements={() => setIsMobileElementsOpen(true)}
            onToggleProperties={() => setIsMobilePropertiesOpen(true)}
            propertiesAvailable={!!primarySelectedObject}
            onOpenHistory={() => setIsHistoryPanelOpen(true)}
            onSaveProject={() => setIsSaveModalOpen(true)}
            onLoadProject={triggerLoadFile}
            onOpenInstructionModal={() => setIsInstructionModalOpen(true)}
          />
        </div>

        <AnimatePresence>
          {contextMenu && (
            <ContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              options={contextMenu.options}
              onClose={closeContextMenu}
            />
          )}
        </AnimatePresence>
        <StatusBar
          zoomLevel={viewTransform.scale / INITIAL_PPM}
          selectedObjectName={statusBarText}
          selectedObjectId={elementTypeToPlace ? "" : state.selectedObjectId}
          currentFloorName={
            state.floors.find((f) => f.id === state.currentFloorId)?.name || ""
          }
        />
        <EnquiryModal
          isOpen={isEnquiryModalOpen}
          onClose={() => setIsEnquiryModalOpen(false)}
          totalCost={totalCost}
          costBreakdown={costBreakdown}
          projectState={state}
          svgRef={svgRef}
          setViewTransform={setViewTransform}
          viewTransform={viewTransform}
          dispatch={dispatch}
        />
        <SaveConfirmModal
          isOpen={isSaveModalOpen}
          onClose={() => setIsSaveModalOpen(false)}
          totalCost={totalCost}
          svgRef={svgRef}
          onSendEnquiry={() => {
            setIsSaveModalOpen(false);
            handleOpenEnquiryModal();
          }}
        />
        <FramelessCreationModal
          isOpen={framelessModalState.isOpen}
          onClose={() =>
            setFramelessModalState({ isOpen: false, position: null })
          }
          onCreate={handleCreateFramelessBuilding}
          initialPosition={framelessModalState.position}
        />
        <InstructionModal
          isOpen={isInstructionModalOpen}
          onClose={() => setIsInstructionModalOpen(false)}
        />
      </div>

      <DragOverlay dropAnimation={null}>
        {activeDragTemplate ? (
          <div style={overlayStyle}>
            <div className="w-24 h-32 bg-white rounded-lg shadow-2xl opacity-80">
              <ModulePreviewRenderer template={activeDragTemplate} />
            </div>
          </div>
        ) : activeDragFrame ? (
          <div style={overlayStyle}>
            <div className="w-32 h-24 bg-white rounded-lg shadow-2xl opacity-80 flex items-center justify-center border border-primary-blue">
              <span className="text-xs font-bold text-primary-blue">{activeDragFrame.name}</span>
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default Configurator;